import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { body, headers, linkStyles, richTextStyles } from '../../styles/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { dotPatternStyles } from '../../styles/backgrounds';

import Button from '../buttons/Button';
import ButtonGroup from '../ButtonGroup';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  padding: ${rem(32)} 0;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  position: relative;
  display: grid;
  gap: ${rem(40)};

  ${media('>small')} {
    grid-template-columns: 1fr 1fr;
  }

  ${media('>smedium')} {
    gap: ${rem(80)};
  }

  ${media('>medium')} {
    gap: ${rem(120)};
  }

  &::before {
    ${dotPatternStyles}
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: ${rem(-20)};
    width: ${rem(270)};
    height: ${rem(112)};
  }
`;

const TextContent = styled.div`
  max-width: ${rem(560)};
`;

const CtaHeadline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `};
`;

const CtaText = styled.div`
  ${richTextStyles}
  color: ${colors.gray['600']};
  margin: ${rem(32)} 0;
`;

const ListContent = styled.div`
  align-self: center;
`;

const CheckListHeadline = styled.h3`
  ${headers.h3}
  margin-bottom: ${rem(24)};
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `};
`;

const List = styled.ul`
  ${body.ul}
`;

const ListItem = styled.li`
  ${linkStyles}
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
  `};
`;

const CtaChecklist = ({
  headline,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  listHeadline,
  checklistItems,
  list,
  backgroundColor
}) => {
  const { pageOverrides } = useTheme();
  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper>
        <TextContent>
          {headline && (
            <CtaHeadline>
              <TextRenderer text={headline} />
            </CtaHeadline>
          )}
          <CtaText darkMode={pageOverrides?.includes('Dark Mode')}>
            <TextRenderer text={text} richText />
          </CtaText>
          <ButtonGroup>
            {primaryCtaText && primaryCtaLink && (
              <Button
                to={primaryCtaLink}
                aria-label={outputIfNoVariables(`${headline} — ${primaryCtaLink}`)}
              >
                <TextRenderer text={primaryCtaText} />
              </Button>
            )}
            {secondaryCtaText && secondaryCtaLink && (
              <ButtonText
                to={secondaryCtaLink}
                aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaLink}`)}
              >
                <TextRenderer text={secondaryCtaText} />
              </ButtonText>
            )}
          </ButtonGroup>
        </TextContent>
        <ListContent>
          {listHeadline && (
            <CheckListHeadline>
              <TextRenderer text={listHeadline} />
            </CheckListHeadline>
          )}
          <List>
            {([...(checklistItems || []), ...(list || [])]).map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={index}>
                <TextRenderer text={item?.text || item} richText={item !== null && typeof item.text === 'object'} />
              </ListItem>
            ))}
          </List>
        </ListContent>
      </ContentWrapper>
    </Container>
  );
};

CtaChecklist.propTypes = {
  headline: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    references: PropTypes.arrayOf(PropTypes.object)
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  listHeadline: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.string),
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment CtaChecklistFragment on ContentfulCtaChecklist {
    id
    __typename
    headline
    text {
      raw
    }
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    listHeadline
    checklistItems {
      text {
        raw
      }
    }
    list
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default CtaChecklist;
